import "ag-grid-community/styles/ag-grid.css"; // Core grid CSS, always needed
import "ag-grid-community/styles/ag-theme-alpine.css"; // Optional theme CSS
import Spinner from "common/components/spinner/spinner";
import { useCallback, useEffect, useState } from "react";
import { useClearCache } from "react-clear-cache";
import { useDispatch, useSelector } from "react-redux";
import { Switch } from "react-router-dom";
import User from "redux/models/user";
import { setInitialSelector } from "redux/slices/globalSelectorSlice";
import {
  selectIsUserTermsAccepted,
  setLoggedInUser,
} from "redux/slices/userSlice";
import getNextAvailableRoute from "routes/nextAvailableRoute";
import RouteWithSubRoutes from "routes/routeWithSubRoutes";
import routes from "routes/routes";
import K from "utilities/constants";
import { LicenseManager } from "ag-grid-enterprise";
import * as Sentry from "@sentry/react";
import { initSentry } from "../sentry/initSentry";
import { EnvironmentAlert } from "./EnvironmentAlert";
import SomethingWentWrong from "../common/components/errorBoundary/somethinWentWrong";
import { getEnvName, isNonProdEnv } from "../utilities/systemUtility";
import { TermsAcceptance } from "../features/termsAcceptance/termsAcceptance";
import { ScrollToTop } from "./ScrollToTop";

initSentry();
LicenseManager.setLicenseKey(K.AgGridTable.TrailKey);

export default function App() {
  useClearCache({ auto: true });

  const dispatch = useDispatch();

  const isTermsAccepted = useSelector(selectIsUserTermsAccepted);

  const [shouldLoad, setShouldLoad] = useState(false);

  const getCurrentUserDetails = useCallback(async () => {
    try {
      const res = await User.getLoggedInUserDetails();
      const user = res.basicDetail;

      const availableRoutes = getNextAvailableRoute(user);

      dispatch(setLoggedInUser({ data: res, homeRoute: availableRoutes[0] }));
      dispatch(
        setInitialSelector({ isSuperAdmin: user.isSuperAdmin, data: res }),
      );
      Sentry.setUser({ id: user.id, email: user.email });

      setShouldLoad(true);
    } catch (err) {
      console.error(err);
    }
  }, [dispatch]);

  useEffect(() => {
    if (isNonProdEnv()) {
      document.title = `${getEnvName()} - ${document.title}`;
    }

    if (
      User.isTokenAvailable() &&
      !(window.location !== window.parent.location)
    )
      getCurrentUserDetails();
    else setShouldLoad(true);
  }, [getCurrentUserDetails]);

  const renderApp = () => {
    if (User.isTokenAvailable() && isTermsAccepted === false) {
      return <TermsAcceptance />;
    }

    return (
      <Switch>
        {routes.map((route, i) => (
          <RouteWithSubRoutes key={i} {...route} exact={route.exact ?? false} />
        ))}
      </Switch>
    );
  };

  return (
    <Sentry.ErrorBoundary fallback={<SomethingWentWrong />}>
      {shouldLoad ? (
        <div className="App">
          <EnvironmentAlert />
          <ScrollToTop />
          {renderApp()}
        </div>
      ) : (
        <Spinner />
      )}
    </Sentry.ErrorBoundary>
  );
}
